import styled from "@emotion/styled";

import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuDropdownSlotName,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentObjectHeroCard } from "@/pages/agent/components/agent-object-hero-card";
import type { TaskDetailViewModel } from "@/pages/agent/task/agent-task-page.utils";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";
import { isClosedAssistantTaskStatus } from "@/store/assistant/assistant-task-status";

export const AgentTaskDetailCard = ({
  taskDetail,
  onOpenProject,
  onOpenActivities,
  onEdit,
  onToggleComplete,
  isUpdatingStatus,
  mutationError,
}: {
  taskDetail: TaskDetailViewModel;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenActivities: ({ taskId }: { taskId: string }) => void;
  onEdit: () => void;
  onToggleComplete: () => void;
  isUpdatingStatus: boolean;
  mutationError: string | null;
}) => {
  const taskTitle = taskDetail.task.title || "Untitled task";
  const description = taskDetail.task.description?.trim();
  const nextFollowUp = taskDetail.nextFollowUp;
  const project = taskDetail.project;
  const isClosed = isClosedAssistantTaskStatus({ status: taskDetail.task.status });
  const activityMenu = [
    {
      id: "view-activity-log",
      kind: MDSMenuContentItemKind.Item,
      label: "View activity log",
      icon: MDSIconKind.History,
      onClick: () => onOpenActivities({ taskId: taskDetail.task.id }),
    },
  ];

  return (
    <AgentObjectHeroCard
      actions={
        <>
          <MDSButton
            isLoading={isUpdatingStatus}
            size={MDSButtonSize.Small}
            variant={isClosed ? MDSButtonVariant.Transparent : MDSButtonVariant.Contrast}
            onPress={onToggleComplete}
          >
            <MDSIcon
              slot={MDSButtonSlotName.LeftIcon}
              kind={isClosed ? MDSIconKind.Refresh : MDSIconKind.Check}
              size={12}
            />
            {isClosed ? "Reopen task" : "Complete task"}
          </MDSButton>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={onEdit}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Pen} size={12} />
            Edit
          </MDSButton>
          <MDSActionMenuDropdown contentList={activityMenu} placement="bottom right">
            <MDSIconButton
              aria-label={`More actions for ${taskTitle}`}
              iconKind={MDSIconKind.More}
              size={MDSIconButtonSize.XSmall}
              slot={MDSMenuDropdownSlotName.Trigger}
              variant={MDSIconButtonVariant.Transparent}
              rounded
            />
          </MDSActionMenuDropdown>
        </>
      }
      ariaLabel={`Task: ${taskTitle}`}
      context={
        project ? (
          <StyledProjectChipButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={() => {
              onOpenProject({ projectId: project.id });
            }}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Folder} size={12} />
            <span>{decodeAgentDisplayText({ value: taskDetail.projectTitle })}</span>
          </StyledProjectChipButton>
        ) : (
          <StyledProjectChipPlaceholder>
            <MDSIcon kind={MDSIconKind.Folder} size={12} />
            <span>{decodeAgentDisplayText({ value: taskDetail.projectTitle })}</span>
          </StyledProjectChipPlaceholder>
        )
      }
      description={description}
      error={mutationError}
      status={taskDetail.task.status}
      title={taskTitle}
    >
      <StyledNextFollowUpLine>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          Next follow-up:
        </MDSText>
        {taskDetail.followUpsLoading ? (
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
            Loading…
          </MDSText>
        ) : nextFollowUp ? (
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            {decodeAgentDisplayText({ value: nextFollowUp.content || "Untitled follow-up" })}
          </MDSText>
        ) : (
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
            None scheduled
          </MDSText>
        )}
      </StyledNextFollowUpLine>
    </AgentObjectHeroCard>
  );
};

const StyledProjectChipButton = styled(MDSButton)`
  background: ${({ theme }) => theme.colors.background.banner.default};
  max-width: 100%;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &[data-hovered] {
    background: ${({ theme }) => theme.colors.background.banner.emphasis};
  }
`;

const StyledProjectChipPlaceholder = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.banner.default};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: inline-flex;
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  gap: ${({ theme }) => theme.spacing[1]};
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledNextFollowUpLine = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;
